/**
 * Authentication utility functions
 * 
 * Uses sessionStorage for tab-isolated authentication.
 * Each browser tab maintains its own independent session.
 */

import { USER_DATA } from "../constants/constants";
import { Permissions, ApiPermissionsResponse } from "@/types/permissions";
import { 
  saveAuthToSession, 
  loadAuthFromSession, 
  clearAuthFromSession,
  getUserPermissionsFromSession,
  getUserPermissionObjectsFromSession,
  updateAuthPermissionsInSession
} from "@/utils/sessionStorageUtils";

// Types
export interface AuthData {
  token?: string;
  user?: {
    email: string;
    name: string;
    id?: string;
    timezone_id?: string;
  };
  role?: string;
  permissions?: Permissions[];
  permissionObjects?: ApiPermissionsResponse;
  [key: string]: any;
}

export interface PermissionsData {
  permissions: Permissions[];
  permissionObjects?: ApiPermissionsResponse;
}

/**
 * Get authentication data from sessionStorage
 */
export const getAuth = (): AuthData | null => {
  if (typeof window === 'undefined') return null;
  
  return loadAuthFromSession();
};

/**
 * Get user permissions from sessionStorage
 */
export const getUserPermissions = (): Permissions[] => {
  return getUserPermissionsFromSession();
};

/**
 * Get user permission objects from sessionStorage
 */
export const getUserPermissionObjects = (): ApiPermissionsResponse => {
  return getUserPermissionObjectsFromSession();
};

/**
 * Set authentication data to sessionStorage
 */
export const setAuth = (authData: AuthData): void => {
  if (typeof window === 'undefined') return;
  
  // Store complete user data including permissions and token
  const userDataToStore = {
    ...authData,
    permissions: authData.permissions || [],
    permissionObjects: authData.permissionObjects || {}
  };
  
  saveAuthToSession(userDataToStore);
};

/**
 * Update permissions in existing auth data
 */
export const updateAuthPermissions = (permissions: Permissions[]): void => {
  if (typeof window === 'undefined') return;
  
  updateAuthPermissionsInSession(permissions);
};

/**
 * Clear authentication data from sessionStorage
 */
export const clearAuth = (): void => {
  if (typeof window === 'undefined') return;
  
  clearAuthFromSession();
};

/**
 * Get authentication token
 */
export const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  
  const userData = getAuth();
  return userData?.token || null;
}; 